export class CompanionRequestError extends Error {
  constructor(message: string, readonly status: number) { super(message); }
}

/** Apply byte limits even to chunked requests without Content-Length. */
export async function readCompanionJson(request: Request, maxBytes = 16_384): Promise<unknown> {
  const origin = request.headers.get("origin");
  if ((origin && origin !== new URL(request.url).origin) || request.headers.get("sec-fetch-site") === "cross-site") {
    throw new CompanionRequestError("Open this form in PeakWave to save changes.", 403);
  }
  if (!request.headers.get("content-type")?.toLowerCase().startsWith("application/json")) throw new CompanionRequestError("Send a JSON request.", 415);
  const reader = request.body?.getReader();
  if (!reader) throw new CompanionRequestError("Add the details to save.", 400);
  const chunks: Uint8Array[] = [];
  let size = 0;
  try {
    while (true) {
      const chunk = await reader.read();
      if (chunk.done) break;
      size += chunk.value.byteLength;
      if (size > maxBytes) { await reader.cancel(); throw new CompanionRequestError("These details are too large to save.", 413); }
      chunks.push(chunk.value);
    }
    const bytes = new Uint8Array(size);
    let offset = 0;
    for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.byteLength; }
    try { return JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(bytes)) as unknown; }
    catch { throw new CompanionRequestError("These details could not be read.", 400); }
  } finally { reader.releaseLock(); }
}

export const COMPANION_PRIVATE_HEADERS = { "Cache-Control": "private, no-store" };

/** Bounded private response reader shared by device draft workflows. */
export async function readCompanionResponse(response: Response, maxBytes = 1048576, errorField: "error" | "message" = "error"): Promise<unknown> {
  const reader = response.body?.getReader();
  if (!reader) throw new Error('The request could not be confirmed. Reconnect and retry.');
  const chunks: Uint8Array[] = []; let size = 0;
  try {
    for (;;) { const part = await reader.read(); if (part.done) break; size += part.value.byteLength;
      if (size > maxBytes) { await reader.cancel(); throw new Error('The private response is too large to check. Reopen this view.'); }
      chunks.push(part.value);
    }
  } finally { reader.releaseLock(); }
  const bytes = new Uint8Array(size); let offset = 0;
  for (const part of chunks) { bytes.set(part, offset); offset += part.byteLength; }
  let value: unknown;
  try { value = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes)); }
  catch { throw new Error('The request could not be confirmed. Reconnect and retry.'); }
  if (!response.ok) {
    const message = value && typeof value === 'object' ? (value as Record<string, unknown>)[errorField] : null;
    throw new Error(typeof message === 'string' ? message.slice(0, 300) : 'The request could not be confirmed. Reconnect and retry.');
  }
  return value;
}
